import { EntityLinks } from '@griply/types';
import { getBarPosition } from '@griply/utils';
import React, { useMemo } from 'react';
import { ArrowLeft, ArrowRight16 } from 'shared/assets/icons';
import { Icon } from 'shared/components/ui/icon';
import { DateRange } from 'shared/types/date-range';
import { Goal } from 'shared/types/goal';
import { ID } from 'shared/types/id';
import { LifeArea } from 'shared/types/life-area';
import { getLinkedItemColor } from 'shared/utils/get-linked-item-color';

import { SCROLL_INTO_VIEW_MARGIN } from './constants';
import {
  getTimelineEdgeArrow,
  TimelineEdgeArrowDirection,
} from './get-timeline-edge-arrow';
import * as Styled from './timeline-edge-arrows.style';
import { SideBarItem } from './types';
import { useTimelineScrollLeft } from './use-timeline-scroll-left';

export type TimelineEdgeArrowsProps = {
  rows: SideBarItem[];
  rowTops: number[];
  goals: Goal[];
  lifeAreas: LifeArea[];
  links: EntityLinks;
  dateRange: DateRange;
  dayWidth: number;
  viewportWidth: number;
  scrollRef: React.RefObject<HTMLDivElement>;
  syncTo: number;
};

type EdgeBar = {
  id: ID;
  top: number;
  left: number;
  width: number;
  color: string | null;
};

export const TimelineEdgeArrows: React.FC<TimelineEdgeArrowsProps> = ({
  rows,
  rowTops,
  goals,
  lifeAreas,
  links,
  dateRange,
  dayWidth,
  viewportWidth,
  scrollRef,
  syncTo,
}) => {
  const scrollLeft = useTimelineScrollLeft(scrollRef, syncTo);

  const bars = useMemo(
    () =>
      rows
        .map((row, index): EdgeBar | null => {
          if (!('goal' in row)) {
            return null;
          }
          const { goal } = row;
          if (!goal.startDate || !goal.deadline) {
            return null;
          }
          const position = getBarPosition({
            startDate: goal.startDate,
            deadline: goal.deadline,
            dateRange,
            dayWidth,
          });
          if (!position) {
            return null;
          }
          return {
            id: goal.id,
            top: rowTops[index],
            left: position.left,
            width: position.width,
            color: getLinkedItemColor(goal.id, { goals, lifeAreas, links }),
          };
        })
        .filter((bar): bar is EdgeBar => bar !== null),
    [rows, rowTops, dateRange, dayWidth, goals, lifeAreas, links],
  );

  return (
    <Styled.Overlay>
      {bars.map((bar) => {
        const direction = getTimelineEdgeArrow(bar, scrollLeft, viewportWidth);
        if (!direction) {
          return null;
        }
        const scrollToBar = () =>
          scrollRef.current?.scrollTo({
            left: Math.max(0, bar.left - SCROLL_INTO_VIEW_MARGIN),
            behavior: 'smooth',
          });
        const icon =
          direction === TimelineEdgeArrowDirection.Left
            ? ArrowLeft
            : ArrowRight16;
        return (
          <Styled.ArrowButton
            key={bar.id}
            $top={bar.top}
            $side={direction}
            onClick={scrollToBar}
          >
            <Styled.ArrowChip>
              <Icon icon={icon} />
            </Styled.ArrowChip>
          </Styled.ArrowButton>
        );
      })}
    </Styled.Overlay>
  );
};
